import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { subscriptionsQuery } from '../../account/queries'
import { subscriptionsToShow } from '../../account/subscription'
import type { Household } from '../../api/households'
import type { SubscriptionStatus } from '../../api/types'
import { avatarUrl, firstName, possessive, type User } from '../../api/users'
import { Button } from '../ui/Button'
import { FormError } from '../ui/FormError'
import { Card, CollapsibleRows, Rows } from './Card'
import { EditableSetting } from './EditableSetting'
import { PasswordSetting } from './PasswordSetting'
import { PublishedGamesSetting } from './PublishedGamesSetting'
import { required } from './Setting'
import { SubscriptionSettings } from './SubscriptionSettings'
import { useOpenStudio } from './useOpenStudio'
import { useUpdateUser } from './useUpdateUser'

const STUDIO_ERROR_MESSAGE = 'We couldn’t open Studio. Please try again.'

/** A status worth a word in the header; the active one is the norm and needs none. */
const STATUS_CHIPS: Partial<Record<SubscriptionStatus, string>> = {
  paused: 'Paused',
  cancelled: 'Cancelled',
}

interface ChildCardProps {
  child: User
  /** Everyone in the household, to say whose kit ships where. */
  kids: User[]
  household: Household
  studioUrl: string
}

/**
 * One child, as a bordered card inside the family card: who they are; then the
 * one thing a parent does most, going into Studio as them, as a full-width
 * section of its own; then their account settings; and last their
 * subscription and billing, closed until asked for. A paused or cancelled
 * subscription still shows as a chip in the header while it is closed.
 */
export function ChildCard({ child, kids, household, studioUrl }: ChildCardProps) {
  const update = useUpdateUser(child)
  const studio = useOpenStudio(child, studioUrl)
  const subscriptions = useQuery(subscriptionsQuery(household))
  const shown = subscriptionsToShow(
    subscriptions.data?.filter((subscription) => subscription.userId === child.id) ?? [],
  )
  const chip = shown[0] && STATUS_CHIPS[shown[0].status]
  const first = firstName(child)

  // Busy through the request and the navigation that follows it, since the
  // parent is leaving the site and switching identity.
  const opening = studio.isPending || studio.isSuccess

  return (
    <Card
      nested
      header={
        <div className="flex flex-wrap items-center gap-4">
          <img
            src={avatarUrl(child.id)}
            alt=""
            width={56}
            height={56}
            className="size-14 shrink-0 rounded-full object-cover"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-xl font-bold text-ink">{child.name}</h3>
              {chip && (
                <span
                  className="rounded-full bg-field px-2 py-0.5 text-xs font-semibold text-ink-soft"
                  data-testid="subscription-status"
                >
                  {chip}
                </span>
              )}
            </div>
            <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-ink-soft">
              Username
              <code className="font-mono text-base text-ink" data-testid="child-username">
                {child.username}
              </code>
              <CopyButton text={child.username} label={`Copy ${possessive(first)} username`} />
            </p>
          </div>
        </div>
      }
      testId={`child-${child.id}`}
    >
      <div className="border-b border-black/5 px-6 py-5" data-testid="studio-section">
        <Button block onClick={() => studio.mutate()} disabled={opening} data-testid="open-studio">
          {opening
            ? `Opening Codeverse Studio as ${child.username}…`
            : `Enter Codeverse Studio as ${child.username}`}
          {!opening && <ArrowIcon />}
        </Button>
        <p className="mt-2 text-center text-sm text-ink-soft" data-testid="studio-hint">
          Log in automatically. No password needed.
        </p>
        {studio.isError && <FormError data-testid="studio-error">{STUDIO_ERROR_MESSAGE}</FormError>}
      </div>
      <Rows heading="Account">
        <EditableSetting
          label="Name"
          value={child.name}
          editLabel={`Edit ${possessive(first)} name`}
          save={(name) => update.mutateAsync({ name })}
          validate={required('Enter a name.')}
          field={{ autoComplete: 'off', autoCapitalize: 'words' }}
          testId="setting-name"
        />
        <PasswordSetting user={child} own={false} testId="setting-password" />
        <PublishedGamesSetting child={child} />
      </Rows>
      <CollapsibleRows heading="Subscription" testId="subscription-section">
        <SubscriptionSettings
          child={child}
          household={household}
          kids={kids}
          subscriptions={subscriptions.data}
          shown={shown}
          error={subscriptions.error}
          retry={() => void subscriptions.refetch()}
        />
      </CollapsibleRows>
    </Card>
  )
}

/** The arrow on the way into Studio. */
function ArrowIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      aria-hidden="true"
      fill="none"
      className="shrink-0"
    >
      <path
        d="M3 9h12m-5-5 5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Copies the username, and says so in place of the icon. */
function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
    } catch {
      // The clipboard is unavailable over plain http and in some embedded
      // browsers. The username is still on screen to be read.
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? 'Copied' : label}
      className="inline-flex h-7 items-center gap-1 rounded-md px-1.5 text-xs font-medium text-ink-soft outline-none transition hover:bg-brand-soft hover:text-brand focus-visible:ring-2 focus-visible:ring-brand-ring"
      data-testid="copy-username"
    >
      <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" fill="none">
        {copied ? (
          <path
            d="m3 8.5 3 3 7-7"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <>
            <rect
              x="5.5"
              y="5.5"
              width="8"
              height="8"
              rx="1.5"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M10.5 5.5v-2a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </>
        )}
      </svg>
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}
